import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '@styles/common/Error.module.css';
import Snackbar from './Snackbar';

interface ErrorProps {
  message?: string;
  title?: string;
  fullPage?: boolean;
  className?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  showLogout?: boolean;
  onLogout?: () => void;
}

/**
 * Error - A reusable error component
 *
 * @param message - Optional error message
 * @param title - Optional error title (default: "Error")
 * @param fullPage - Whether to render as full page (default: true)
 * @param className - Optional additional CSS class
 * @param showRetry - Whether to show retry button (default: false)
 * @param onRetry - Optional retry callback function
 *
 * @example
 * <Error message="Failed to load data" />
 *
 * @example
 * <Error message="Something went wrong" showRetry onRetry={handleRetry} />
 *
 * @example
 * <Error message="Error loading..." fullPage={false} />
 */
const Error: React.FC<ErrorProps> = ({
  message,
  title = 'Error',
  fullPage = true,
  className,
  showRetry = false,
  onRetry,
  showLogout = false,
  onLogout,
}) => {
  const { t } = useTranslation();
  const defaultTitle = t('error.title');
  const displayTitle = title === 'Error' ? defaultTitle : title;

  // When fullPage is false, show as snackbar overlay at bottom
  if (!fullPage && message) {
    return (
      <Snackbar
        message={
          displayTitle && displayTitle !== defaultTitle
            ? `${displayTitle}: ${message}`
            : message
        }
        variant='error'
        position='bottom'
        dismissable={!showRetry}
        duration={showRetry ? 'permanent' : 8000}
        onClose={showRetry ? undefined : onRetry}
        actionLabel={showRetry ? t('error.retry') : undefined}
        onAction={showRetry ? onRetry : undefined}
        className={className}
      />
    );
  }

  const containerClass = fullPage
    ? `${styles.fullPageContainer} ${className || ''}`
    : `${styles.container} ${className || ''}`;

  return (
    <div className={containerClass} role='alert'>
      <div className={styles.errorIcon}>
        <svg
          width='48'
          height='48'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <circle cx='12' cy='12' r='10'></circle>
          <line x1='12' y1='8' x2='12' y2='12'></line>
          <line x1='12' y1='16' x2='12.01' y2='16'></line>
        </svg>
      </div>
      <h2 className={styles.title}>{displayTitle}</h2>
      {message && <p className={styles.message}>{message}</p>}
      {showRetry && onRetry && (
        <button className={styles.retryButton} onClick={onRetry} type='button'>
          {t('error.retry')}
        </button>
      )}
      {showLogout && onLogout && (
        <button className={styles.retryButton} onClick={onLogout} type='button'>
          {t('auth.logout')}
        </button>
      )}
    </div>
  );
};

export default Error;
